import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button, InputText, Text } from "@pentabd/ui";

import { InputDateController, SelectEmployee } from "@components";

import { useMaintenanceById } from "@hooks/inventory/maintenance/useMaintenanceById";
import { useUser } from "@hooks/useUser";

import {
  MAINTENANCE_VALIDATION,
  MaintenanceDetailValidationSchemaType,
  maintenanceDetailValidationSchema,
} from "@validations/Inventory/maintenance/maintenance";

import {
  MaintenanceType,
  UpdateMaintenanceType,
} from "@type/inventory/maintenance";

import { getAccess } from "@helpers/permissionController";

import { MAINTENANCE_STATUS } from "@constants/status.hrm";
import { useLanguage } from "@hooks/useLanguage";
import { useEffect } from "react";

const { FIXED, PENDING } = MAINTENANCE_STATUS;

export default function MaintenanceDetailsForm() {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { updateMaintenanceHandler, maintenanceState } = useMaintenanceById();

  const singleMaintenance = maintenanceState.getSingleData
    ?.data as MaintenanceType;

  const method = useForm<MaintenanceDetailValidationSchemaType>({
    resolver: yupResolver(maintenanceDetailValidationSchema),
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = method;

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { assignedEmployee, createdByEmployee } = singleMaintenance || {};
  const { nameEn, nameBn } = assignedEmployee || {};

  useEffect(() => {
    if (assignedEmployee) {
      setValue("check", 2);
    } else {
      setValue("check", 1);
    }
  }, [assignedEmployee]);

  const onSubmit = (data: any, status: string) => {
    const __data = {
      employeeId: data.employeeId,
      bookStartDate: data.bookStartDate,
      bookEndDate: data.bookEndDate,
      status,
    };

    updateMaintenanceHandler(__data as UpdateMaintenanceType);
  };

  return (
    <div className="container p-9 g-10">
      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.CATEGORY.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="category"
            defaultValue={singleMaintenance?.category || ""}
            disabled
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.GROUP.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="group"
            defaultValue={singleMaintenance?.group || ""}
            disabled
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.BRAND.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="product_name"
            defaultValue={singleMaintenance?.brand || ""}
            disabled
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.NAME.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="group"
            defaultValue={singleMaintenance?.product || ""}
            disabled
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.WARRANTY_DATE")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7 d-flex justify-content-between gap-10">
          <div className="flex-fill">
            <InputDateController
              control={control}
              name="warranty_date"
              placeholder={singleMaintenance?.warrantyStartDate}
              disabled
            />
          </div>
          <div className="flex-fill">
            <InputDateController
              control={control}
              name="warranty_date"
              placeholder={singleMaintenance?.warrantyEndDate}
              disabled
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.ISSUE_NAME.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="group"
            defaultValue={singleMaintenance?.issueName || ""}
            disabled
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.ISSUE_NO.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="group"
            defaultValue={singleMaintenance?.issueNo.toString() || ""}
            disabled
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("INVENTORY_MAINTENANCE.FORM.ISSUE_DETAILS.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <InputText
            name="group"
            defaultValue={singleMaintenance?.issueDetails || ""}
            disabled
          />
        </div>
      </div>

      <FormProvider {...method}>
        <form>
          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t("INVENTORY_MAINTENANCE.FORM.ASSIGNED_PERSON")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              {getAccess(roles, "ASSIGN_MAINTENANCE") &&
              !singleMaintenance?.assignedEmployee ? (
                <>
                  <SelectEmployee
                    selectSingleEmployee
                    registeredName={MAINTENANCE_VALIDATION.EMPLOYEE_ID}
                  />
                  <Text className="text-danger" size="sm">
                    {errors[MAINTENANCE_VALIDATION.EMPLOYEE_ID]?.message}
                  </Text>
                </>
              ) : (
                <>
                  <InputText
                    name="group"
                    defaultValue={language === "bn" ? nameBn : nameEn}
                    disabled
                  />
                </>
              )}
            </div>
          </div>

          {getAccess(roles, "ASSIGN_MAINTENANCE") ||
          user?.employeeMasterId ===
            singleMaintenance?.assignedEmployee?.id.toString() ||
          (user?.employeeMasterId === createdByEmployee?.id.toString() &&
            assignedEmployee) ? (
            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("INVENTORY_MAINTENANCE.FORM.BOOK_START_DATE.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputDateController
                  control={control}
                  name={MAINTENANCE_VALIDATION.BOOK_START_DATE}
                  errors={errors}
                  placeholder={
                    singleMaintenance?.bookStartDate ||
                    t("INVENTORY_MAINTENANCE.FORM.BOOK_START_DATE.PLACEHOLDER")
                  }
                  disabled={
                    singleMaintenance?.bookStartDate ||
                    !getAccess(roles, "ASSIGN_MAINTENANCE")
                  }
                />
              </div>
            </div>
          ) : (
            <></>
          )}

          {getAccess(roles, "ASSIGN_MAINTENANCE") ||
          user?.employeeMasterId ===
            singleMaintenance?.assignedEmployee?.id.toString() ||
          (user?.employeeMasterId === createdByEmployee?.id.toString() &&
            assignedEmployee) ? (
            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("INVENTORY_MAINTENANCE.FORM.BOOK_END_DATE.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputDateController
                  control={control}
                  name={MAINTENANCE_VALIDATION.BOOK_END_DATE}
                  errors={errors}
                  placeholder={
                    singleMaintenance?.bookEndDate ||
                    t("INVENTORY_MAINTENANCE.FORM.BOOK_END_DATE.PLACEHOLDER")
                  }
                  disabled={
                    singleMaintenance?.bookEndDate ||
                    !getAccess(roles, "ASSIGN_MAINTENANCE")
                  }
                />
              </div>
            </div>
          ) : (
            <></>
          )}

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title"></Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <div className="d-flex justify-content-end gap-8">
                {getAccess(roles, "ASSIGN_MAINTENANCE") &&
                !singleMaintenance?.assignedEmployee ? (
                  <Button
                    fill="fill"
                    type="info"
                    size="md"
                    htmlType="submit"
                    onClick={handleSubmit((data) => onSubmit(data, PENDING))}
                  >
                    {t("INVENTORY_MAINTENANCE.FORM.SAVE_BUTTON_TEXT")}
                  </Button>
                ) : (
                  <></>
                )}

                {user?.employeeMasterId ===
                  singleMaintenance?.assignedEmployee?.id.toString() &&
                singleMaintenance?.status !== FIXED ? (
                  <Button
                    fill="fill"
                    type="info"
                    size="md"
                    htmlType="submit"
                    onClick={handleSubmit((data) => onSubmit(data, FIXED))}
                  >
                    {t("INVENTORY_MAINTENANCE.FORM.FIXED_BUTTON_TEXT")}
                  </Button>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
